export interface CollectionSelectionState {
  active: boolean;
  selectedIds: Set<string>;
  lastSelectedId: string | null;
}

export type CollectionSelectionAction =
  | { type: 'enter' }
  | { type: 'clear' }
  | { type: 'toggle'; id: string; shiftKey?: boolean; visibleIds?: string[] }
  | { type: 'selectRange'; targetId: string; visibleIds: string[] }
  | { type: 'toggleVisible'; ids: string[] }
  | { type: 'select'; ids: string[] }
  | { type: 'reconcile'; validIds: string[] };

export const initialCollectionSelectionState: CollectionSelectionState = {
  active: false,
  selectedIds: new Set(),
  lastSelectedId: null,
};

const setsEqual = (left: Set<string>, right: Set<string>) =>
  left.size === right.size && [...left].every((id) => right.has(id));

const withSelection = (
  state: CollectionSelectionState,
  selectedIds: Set<string>,
  lastSelectedId: string | null = state.lastSelectedId,
): CollectionSelectionState => {
  const active = selectedIds.size > 0;
  const validLastSelectedId =
    active && lastSelectedId && selectedIds.has(lastSelectedId)
      ? lastSelectedId
      : active && selectedIds.size > 0
        ? [...selectedIds][selectedIds.size - 1]
        : null;

  return active === state.active &&
    validLastSelectedId === state.lastSelectedId &&
    setsEqual(selectedIds, state.selectedIds)
    ? state
    : { active, selectedIds, lastSelectedId: validLastSelectedId };
};

export function collectionSelectionReducer(
  state: CollectionSelectionState,
  action: CollectionSelectionAction,
): CollectionSelectionState {
  switch (action.type) {
    case 'enter':
      return state.active
        ? state
        : { active: true, selectedIds: new Set(), lastSelectedId: null };
    case 'clear':
      return state.active || state.selectedIds.size
        ? initialCollectionSelectionState
        : state;
    case 'toggle': {
      if (
        action.shiftKey &&
        action.visibleIds &&
        action.visibleIds.length > 0
      ) {
        return collectionSelectionReducer(state, {
          type: 'selectRange',
          targetId: action.id,
          visibleIds: action.visibleIds,
        });
      }
      const selectedIds = new Set(state.selectedIds);
      let nextLastSelectedId: string | null = state.lastSelectedId;

      if (selectedIds.has(action.id)) {
        selectedIds.delete(action.id);
        if (nextLastSelectedId === action.id) {
          nextLastSelectedId = null;
        }
      } else {
        selectedIds.add(action.id);
        nextLastSelectedId = action.id;
      }
      return withSelection(state, selectedIds, nextLastSelectedId);
    }
    case 'selectRange': {
      const { targetId, visibleIds } = action;
      const selectedIds = new Set(state.selectedIds);
      const anchorId = state.lastSelectedId;

      const targetIndex = visibleIds.indexOf(targetId);
      const anchorIndex = anchorId ? visibleIds.indexOf(anchorId) : -1;

      if (targetIndex !== -1 && anchorIndex !== -1) {
        const start = Math.min(anchorIndex, targetIndex);
        const end = Math.max(anchorIndex, targetIndex);
        const rangeIds = visibleIds.slice(start, end + 1);
        rangeIds.forEach((id) => selectedIds.add(id));
      } else if (targetIndex !== -1) {
        selectedIds.add(targetId);
      }

      return withSelection(state, selectedIds, targetId);
    }
    case 'toggleVisible': {
      const selectedIds = new Set(state.selectedIds);
      const allVisibleSelected =
        action.ids.length > 0 && action.ids.every((id) => selectedIds.has(id));
      action.ids.forEach((id) => {
        if (allVisibleSelected) selectedIds.delete(id);
        else selectedIds.add(id);
      });
      const nextLastSelectedId =
        action.ids[action.ids.length - 1] || state.lastSelectedId;
      return withSelection(state, selectedIds, nextLastSelectedId);
    }
    case 'select': {
      const selectedIds = new Set(state.selectedIds);
      action.ids.forEach((id) => selectedIds.add(id));
      const nextLastSelectedId =
        action.ids[action.ids.length - 1] || state.lastSelectedId;
      return withSelection(state, selectedIds, nextLastSelectedId);
    }
    case 'reconcile': {
      const validIds = new Set(action.validIds);
      const nextSelectedIds = new Set(
        [...state.selectedIds].filter((id) => validIds.has(id)),
      );
      const nextLastSelectedId =
        state.lastSelectedId && validIds.has(state.lastSelectedId)
          ? state.lastSelectedId
          : null;
      return withSelection(state, nextSelectedIds, nextLastSelectedId);
    }
    default:
      return state;
  }
}
